import React, { type FunctionComponent, type SVGProps } from 'react';

import { Action, IconWrapper, Label, Option, StyledFieldToggle } from './FieldToggle.style';

type SelectedId = string;
type SvgIconComponent = FunctionComponent<SVGProps<SVGSVGElement>>;
interface ToggleOptions {
    id: SelectedId;
    icon: SvgIconComponent;
}

export interface FieldToggleProps {
    // If we ever want to support more than 2 options,
    // we would need to make each an individual button.
    options: [ToggleOptions, ToggleOptions];
    selected: SelectedId;
    label?: string;
    ariaLabel?: string;
    backdropBlur?: boolean;
    disabled?: boolean;
    onToggle?: (newSelection: SelectedId) => void;
}

// NOTE: If we ever want to support variable width options,
// we will need to use `framer-motion` to animate the highlight.
export function FieldToggle({
    options,
    selected,
    label,
    ariaLabel,
    backdropBlur = false,
    disabled = false,
    onToggle,
}: FieldToggleProps) {
    const selectedIndex = options.findIndex(({ id }) => selected === id);
    const firstOptionSelected = selectedIndex === 0;
    const isDisabled = disabled || !onToggle;

    function handleAction() {
        if (!onToggle) return;
        const newSelection = firstOptionSelected ? options[1].id : options[0].id;
        onToggle(newSelection);
    }

    const optionsMarkup = options.map(({ id, icon }) => {
        const IconElement = icon;

        return (
            <Option key={id}>
                <IconWrapper>
                    <IconElement />
                </IconWrapper>
            </Option>
        );
    });

    const labelMarkup = label?.length ? <Label>{label}</Label> : null;

    return (
        <StyledFieldToggle $backdropBlur={backdropBlur} $border={Boolean(labelMarkup)} $disabled={isDisabled}>
            {labelMarkup}

            <Action ariaLabel={ariaLabel} disabled={isDisabled} pressed={!firstOptionSelected} onAction={handleAction}>
                {optionsMarkup}
            </Action>
        </StyledFieldToggle>
    );
}
